/**
 * INFO OG VEDLEGG PÅ RESSURS, POST OG VAKT (QA13 #51, #42, grep C3, migrering 0925).
 *
 * Tre deler, gjenbrukt der de finnes:
 *   notat     `resources.notes`, `staffing_roles.note`, `shifts.note`
 *   filer     `attachments` med eiertypene `resource`, `staffing_role` og `shift`
 *   lenker    `info_links` (denne fila)
 *
 * ARV NEDOVER: den som står på vakta, leser vaktas, postens og ressursens info samlet
 * (`GET /v1/shifts/:id/info`), merket med hvor den kom fra.
 */
import { z } from 'zod';

import { attachmentDtoSchema } from './attachments.js';
import { shiftContactSchema } from './people.js';

export const INFO_OWNER_TYPES = ['resource', 'staffing_role', 'shift'] as const;
export type InfoOwnerType = (typeof INFO_OWNER_TYPES)[number];
export const infoOwnerTypeSchema = z.enum(INFO_OWNER_TYPES).meta({ id: 'InfoOwnerType' });

/** Hvor mange lenker én ting kan ha. Info er noen få pekere, ikke et arkiv. */
export const MAX_INFO_LINKS = 30;

export const infoLinkSchema = z
  .object({
    id: z.uuid(),
    url: z.string(),
    label: z.string().nullable(),
  })
  .meta({ id: 'InfoLink' });
export type InfoLink = z.infer<typeof infoLinkSchema>;

export const listInfoLinksQuerySchema = z
  .object({ ownerType: infoOwnerTypeSchema, ownerId: z.uuid() })
  .meta({ id: 'ListInfoLinksQuery' });
export type ListInfoLinksQuery = z.infer<typeof listInfoLinksQuerySchema>;

export const createInfoLinkRequestSchema = z
  .object({
    ownerType: infoOwnerTypeSchema,
    ownerId: z.uuid(),
    url: z
      .string()
      .trim()
      .max(2000)
      .regex(/^https?:\/\//iu, 'Use a web address starting with http:// or https://.'),
    label: z.string().trim().min(1).max(200).nullish(),
  })
  .meta({ id: 'CreateInfoLinkRequest' });
export type CreateInfoLinkRequest = z.infer<typeof createInfoLinkRequestSchema>;

export const infoLinkListResponseSchema = z
  .object({ items: z.array(infoLinkSchema) })
  .meta({ id: 'InfoLinkListResponse' });
export type InfoLinkListResponse = z.infer<typeof infoLinkListResponseSchema>;

/** Én del av det samlede kortet: notatet, filene og lenkene fra ett sted. */
export const infoPartSchema = z
  .object({
    id: z.uuid(),
    /** Ressursens eller postens navn; vaktas tittel for vakta selv. */
    name: z.string(),
    note: z.string().nullable(),
    files: z.array(attachmentDtoSchema),
    links: z.array(infoLinkSchema),
  })
  .meta({ id: 'InfoPart' });
export type InfoPart = z.infer<typeof infoPartSchema>;

/**
 * DET DEN SOM STÅR PÅ VAKTA SER (51, 42). Rekkefølgen er arven: ressursen («Fra Bar stornaust»),
 * posten, og til slutt vakta selv («For denne vakta»). En del uten innhold er null eller tom.
 */
export const shiftInfoSchema = z
  .object({
    resource: infoPartSchema.nullable(),
    post: infoPartSchema.nullable(),
    shift: infoPartSchema,
    contact: shiftContactSchema.nullable(),
  })
  .meta({ id: 'ShiftInfo' });
export type ShiftInfo = z.infer<typeof shiftInfoSchema>;

/**
 * KONTAKTPERSONER HOS FESTIVALEN FOR BANDET (QA13 #109, 253.45–253.46) — en avledet liste.
 *
 * Én rad per menneske, med alle grunnene: ansvarlig for scenen, ansvarlig for en ressurs bandet
 * bruker (bil, rom, spisested), sjåfør på en av reisene, kontaktperson på delingen, eller lagt til
 * for hånd. For en bil står både den ansvarlige og sjåføren, hver med sin grunn (253.46).
 */
export const FESTIVAL_CONTACT_REASONS = [
  'stage_owner',
  'resource_owner',
  'driver',
  'share_contact',
  'manual',
] as const;
export type FestivalContactReason = (typeof FESTIVAL_CONTACT_REASONS)[number];

export const festivalContactSchema = z
  .object({
    personId: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    reasons: z.array(
      z.object({
        reason: z.enum(FESTIVAL_CONTACT_REASONS),
        /** Ressursens navn, eller dagen (ISO) for en sjåfør. */
        ref: z.string().nullable(),
        /** Bilen sjåføren kjører. */
        refName: z.string().nullable(),
        /**
         * Ressurstypens eget navn for `resource_owner` («Rom», «Kjøretøy»), så Kilde kan si «fra rom»
         * uten at koden kjenner typene (visjonsregel 5, 1345). Valgfri for eldre svar og fiksturer.
         */
        refType: z.string().nullable().optional(),
      }),
    ),
  })
  .meta({ id: 'FestivalContact' });
export type FestivalContact = z.infer<typeof festivalContactSchema>;

export const festivalContactListResponseSchema = z
  .object({ items: z.array(festivalContactSchema) })
  .meta({ id: 'FestivalContactListResponse' });
export type FestivalContactListResponse = z.infer<typeof festivalContactListResponseSchema>;
